import { AuthEvents } from '@/lib/auth-events'
import { authStorage } from '@/lib/auth-storage'

const API_BASE_URL = '/api/v1'

export interface ApiResponse<T = unknown> {
	code: number
	message: string
	data: T
}

export interface PageData<T> {
	list: T[]
	total: number
	page: number
	page_size: number
}

export class ApiError extends Error {
	code: number
	status: number
	requestId: string

	constructor(options: { code: number; message: string; status?: number; requestId?: string }) {
		super(options.message)
		this.name = 'ApiError'
		this.code = options.code
		this.status = options.status ?? 0
		this.requestId = options.requestId ?? ''
	}
}

class ApiClient {
	private baseUrl: string

	constructor(baseUrl: string) {
		this.baseUrl = baseUrl
	}

	private getHeaders(): HeadersInit {
		const headers: HeadersInit = {
			'Content-Type': 'application/json',
		}
		const token = authStorage.getToken()
		if (token) {
			headers.Authorization = `Bearer ${token}`
		}
		return headers
	}

	private getAuthHeaders(): HeadersInit {
		const headers: HeadersInit = {}
		const token = authStorage.getToken()
		if (token) {
			headers.Authorization = `Bearer ${token}`
		}
		return headers
	}

	private getAuthJsonHeaders(): HeadersInit {
		return this.getHeaders()
	}

	private async parseResponse<T>(response: Response): Promise<ApiResponse<T>> {
		const requestId = response.headers.get('X-Request-ID') ?? ''
		const contentType = response.headers.get('content-type') ?? ''
		if (!contentType.includes('application/json')) {
			if (response.status === 401) {
				AuthEvents.triggerSessionExpired()
			}
			throw new ApiError({
				code: response.ok ? 10005 : response.status,
				status: response.status,
				requestId,
				message: response.ok ? '服务器响应格式错误' : `请求失败 (${response.status})`,
			})
		}

		const data = (await response.json()) as ApiResponse<T>
		if (!response.ok || data.code !== 0) {
			const error = new ApiError({
				code: data.code || response.status,
				status: response.status,
				requestId,
				message: data.message || `请求失败 (${response.status})`,
			})
			if (error.code === 10002 || error.status === 401) {
				AuthEvents.triggerSessionExpired()
			}
			throw error
		}

		return data
	}

	async request<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
		const url = `${this.baseUrl}${endpoint}`
		let response: Response
		try {
			response = await fetch(url, {
				...options,
				headers: {
					...this.getHeaders(),
					...options?.headers,
				},
			})
		} catch {
			throw new ApiError({ code: 0, status: 0, message: '网络连接失败，请检查网络后重试' })
		}

		return this.parseResponse<T>(response)
	}

	get<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, { ...options, method: 'GET' })
	}

	post<T>(endpoint: string, body?: unknown, options?: RequestInit): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			...options,
			method: 'POST',
			body: body ? JSON.stringify(body) : undefined,
		})
	}

	put<T>(endpoint: string, body?: unknown, options?: RequestInit): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			...options,
			method: 'PUT',
			body: body ? JSON.stringify(body) : undefined,
		})
	}

	delete<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, { ...options, method: 'DELETE' })
	}

	async upload<T>(
		endpoint: string,
		formData: FormData,
		options?: RequestInit
	): Promise<ApiResponse<T>> {
		const url = `${this.baseUrl}${endpoint}`
		let response: Response
		try {
			response = await fetch(url, {
				...options,
				method: options?.method ?? 'POST',
				headers: {
					...this.getAuthHeaders(),
					...options?.headers,
				},
				body: formData,
			})
		} catch {
			throw new ApiError({ code: 0, status: 0, message: '网络连接失败，请检查网络后重试' })
		}

		return this.parseResponse<T>(response)
	}

	async download(endpoint: string, body?: unknown, options?: RequestInit): Promise<void> {
		const url = `${this.baseUrl}${endpoint}`
		let response: Response
		try {
			response = await fetch(url, {
				...options,
				method: options?.method ?? 'POST',
				headers: {
					...this.getAuthJsonHeaders(),
					...options?.headers,
				},
				body: body ? JSON.stringify(body) : undefined,
			})
		} catch {
			throw new ApiError({ code: 0, status: 0, message: '网络连接失败，请检查网络后重试' })
		}

		if (!response.ok) {
			const contentType = response.headers.get('content-type') ?? ''
			if (contentType.includes('application/json')) {
				await this.parseResponse(response)
			}
			throw new ApiError({ code: response.status, status: response.status, message: `下载失败 (${response.status})` })
		}

		const blob = await response.blob()
		const fileName = getDownloadFileName(response.headers.get('Content-Disposition')) || 'download.xlsx'
		const objectUrl = window.URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = objectUrl
		link.download = fileName
		document.body.appendChild(link)
		link.click()
		link.remove()
		window.URL.revokeObjectURL(objectUrl)
	}

	async blob(endpoint: string, options?: RequestInit): Promise<Blob> {
		const url = `${this.baseUrl}${endpoint}`
		let response: Response
		try {
			response = await fetch(url, {
				...options,
				method: options?.method ?? 'GET',
				headers: {
					...this.getAuthHeaders(),
					...options?.headers,
				},
			})
		} catch {
			throw new ApiError({ code: 0, status: 0, message: '网络连接失败，请检查网络后重试' })
		}

		if (!response.ok) {
			const contentType = response.headers.get('content-type') ?? ''
			if (contentType.includes('application/json')) {
				await this.parseResponse(response)
			}
			throw new ApiError({ code: response.status, status: response.status, message: `请求失败 (${response.status})` })
		}

		return response.blob()
	}

	/** 发送请求并以纯文本返回响应体（不做 JSON 解析） */
	async fetchRawText(endpoint: string, body?: unknown, options?: RequestInit): Promise<string> {
		const url = `${this.baseUrl}${endpoint}`
		let response: Response
		try {
			response = await fetch(url, {
				...options,
				method: options?.method ?? 'POST',
				headers: {
					...this.getAuthJsonHeaders(),
					...options?.headers,
				},
				body: body ? JSON.stringify(body) : undefined,
			})
		} catch {
			throw new ApiError({ code: 0, status: 0, message: '网络连接失败，请检查网络后重试' })
		}

		if (!response.ok) {
			const contentType = response.headers.get('content-type') ?? ''
			if (contentType.includes('application/json')) {
				await this.parseResponse(response)
			}
			throw new ApiError({ code: response.status, status: response.status, message: `请求失败 (${response.status})` })
		}

		return response.text()
	}
}

export const apiClient = new ApiClient(API_BASE_URL)
export { API_BASE_URL }

function getDownloadFileName(contentDisposition: string | null): string {
	if (!contentDisposition) return ''
	const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/)
	if (utf8Match?.[1]) {
		return decodeURIComponent(utf8Match[1])
	}
	const match = contentDisposition.match(/filename="?([^";]+)"?/)
	return match?.[1] ?? ''
}
